/**
 * Announcement interaction events (viewed/closed/CTA clicks).
 *
 * Unlike the rest of the service layer these do not go through BASE_URL. The endpoint
 * lives on the dashboard host that rendered the banner and authenticates with the Rails
 * session plus the signed render token, so the request stays same-origin and carries the
 * CSRF token instead of a JWT. `eventsUrl` and `renderToken` both come from the
 * server-rendered announcement props (see AnnouncementsHelper#announcement_component_props).
 */

export const ANNOUNCEMENT_EVENTS = {
  VIEWED: 'viewed',
  CLOSED: 'closed',
  PRIMARY_CTA: 'primary_cta',
  SECONDARY_CTA: 'secondary_cta',
  // The plans dialog appeared - the step after the click.
  BILLING_VIEWED: 'billing_viewed',
}

/**
 * Fire an announcement interaction event.
 *
 * Tracking must never block or break the UI, so a missing url/token is a no-op and the
 * caller is expected to swallow rejections. `keepalive` lets the request survive the
 * navigation a CTA click triggers.
 *
 * @param {object} params
 * @param {string} params.eventsUrl - announcement events path, e.g. /announcements/1/events
 * @param {string} params.renderToken - signed token proving the server rendered this announcement
 * @param {string} params.event - one of ANNOUNCEMENT_EVENTS
 * @param {string} [params.csrf] - Rails CSRF token
 * @param {boolean} [params.keepalive]
 * @returns {Promise<Response|null>}
 */
export const recordAnnouncementEvent = ({ eventsUrl, renderToken, event, csrf, keepalive = true }) => {
  if (!eventsUrl || !renderToken) return Promise.resolve(null)

  return fetch(eventsUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(csrf && { 'X-CSRF-Token': csrf }),
    },
    body: JSON.stringify({
      event,
      render_token: renderToken,
      page_path: typeof window === 'undefined' ? null : window.location.pathname,
    }),
    keepalive,
  })
}
